import { z } from "zod";

export const orderHttpsUrlSchema = z
  .url()
  .max(2048)
  .regex(/^https:\/\/[^/?#@\s]+(?:[/?#][^\s]*)?$/)
  .meta({ format: "uri" });
const shippingFacts = z.strictObject({
  fulfillments: z
    .array(
      z.strictObject({
        status: z.string().max(64).nullable(),
        latest_status: z.string().max(64).nullable(),
        latest_event: z
          .strictObject({
            status: z.string().max(64),
            happened_at: z.iso.datetime({ offset: true }),
          })
          .nullable(),
        updated_at: z.iso.datetime({ offset: true }).nullable(),
        eta: z.iso.datetime({ offset: true }).nullable(),
        requires_shipping: z.boolean().nullable(),
        trackings: z
          .array(
            z.strictObject({
              carrier: z.string().max(512).nullable(),
              number: z.string().max(512).nullable(),
              url: orderHttpsUrlSchema.optional(),
            }),
          )
          .max(5),
        remaining_trackings: z.int().nonnegative(),
      }),
    )
    .max(8),
  remaining_fulfillments: z.int().nonnegative(),
  more_fulfillments: z.boolean(),
  cancelled_at: z.iso.datetime({ offset: true }).nullable().optional(),
  refunds: z
    .array(
      z.strictObject({
        created_at: z.iso.datetime({ offset: true }).nullable(),
        amount: z
          .string()
          .regex(/^-?\d+(?:\.\d+)?$/)
          .max(64),
        currency: z.string().regex(/^[A-Z]{3}$/),
      }),
    )
    .max(5),
  remaining_refunds: z.int().nonnegative(),
  returns: z
    .array(
      z.strictObject({
        name: z.string().max(512).nullable(),
        status: z.string().max(64).nullable(),
        created_at: z.iso.datetime({ offset: true }).nullable(),
      }),
    )
    .max(5),
  remaining_returns: z.int().nonnegative(),
  more_returns: z.boolean(),
  refund_bank_settlement: z.literal("unknown"),
});
/** 整单状态：后端按“取消、数量未知、送达、已发、部分已发、未发”的顺序算出的结论，挂件只负责翻成文字。 */
export const orderStatusSchema = z.enum([
  "canceled",
  "unknown",
  "delivered",
  "shipped",
  "partially_shipped",
  "unfulfilled",
]);
export type OrderStatus = z.infer<typeof orderStatusSchema>;
export const orderSummarySchema = z.strictObject({
  image_url: orderHttpsUrlSchema.optional(),
  order_ref: z.string().min(1).max(512),
  name: z.string().min(1).max(256),
  created_at: z.iso.datetime({ offset: true }),
  financial_status: z.string().max(64).nullable(),
  fulfillment_status: z.string().max(64),
  total: z.strictObject({
    amount: z
      .string()
      .regex(/^-?\d+(?:\.\d+)?$/)
      .max(64),
    currency: z.string().regex(/^[A-Z]{3}$/),
  }),
});
const envelope = {
  card_id: z.string().min(1).max(256),
  version: z.int().positive(),
  updated_at: z.iso.datetime(),
};
const fetchedAt = z.iso.datetime();
export const orderCardSchema = z.union([
  z.strictObject({
    ...envelope,
    type: z.literal("order_list"),
    lifecycle: z.literal("active"),
    payload: z.strictObject({
      orders: z.array(orderSummarySchema).max(50),
      limit_reached: z.boolean().optional(),
      next_cursor: z.string().min(1).max(2048).nullable(),
      fetched_at: fetchedAt,
    }),
  }),
  z.strictObject({
    ...envelope,
    type: z.literal("order_detail"),
    lifecycle: z.literal("active"),
    payload: orderSummarySchema.extend({
      order_status: orderStatusSchema,
      items: z
        .array(
          z.strictObject({
            title: z.string().max(512),
            quantity: z.int().nonnegative(),
            variant: z.string().max(512).nullable().optional(),
            image_url: orderHttpsUrlSchema.optional(),
            requires_shipping: z.boolean().nullable().optional(),
            shipped_quantity: z.int().nonnegative().optional(),
            fulfilled_quantity: z.int().nonnegative().optional(),
            pending_quantity: z.int().nonnegative().optional(),
          }),
        )
        .max(20),
      remaining_items: z.int().nonnegative(),
      shipping: shippingFacts.optional(),
      order_status_url: orderHttpsUrlSchema.optional(),
      fetched_at: fetchedAt,
      missing_fields: z.array(z.string()),
      actions: z.array(z.enum(["contact_store", "not_received"])),
    }),
  }),
  z.strictObject({
    ...envelope,
    type: z.enum(["order_list", "order_detail"]),
    lifecycle: z.enum(["expired", "revoked"]),
    payload: z.strictObject({}),
  }),
]);
export type OrderCard = z.infer<typeof orderCardSchema>;
